import { type AudienceKey } from '@yoyoclub/shared';
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { HomeFilterRow, HomeFilterSheet } from '../components/home/HomeFilters';
import { GiftStrip, HostCta, Testimonials, WhySection } from '../components/home/HomeSections';
import RecommendationBlock, {
  type RecommendationView,
} from '../components/home/RecommendationBlock';
import StageSelector from '../components/home/StageSelector';
import {
  AUDIENCE_THEME,
  parseSelection,
  recommendationsQuery,
  selectionHash,
  selectionWindow,
  type HomeSelection,
} from '../components/home/homeState';
import type { PickCardData } from '../components/picks/PickCard';
import Sheet from '../components/ui/Sheet';
import type { PublicCourse } from '../services/courseApi';
import { transformActivity, transformCourse } from '../utils/activityTransform';
import { ptDayEndUtcMs, ptDayStartUtcMs } from '../utils/timezone';

type RecommendedItem =
  | (Parameters<typeof transformActivity>[0] & { itemType: 'activity' })
  | (PublicCourse & { itemType: 'course' });

interface RecommendationsResponse {
  activities: RecommendedItem[];
  activitiesTotal: number;
  inPersonTotal: number;
  picks: PickCardData[];
  stageActivityCount: number | null;
}

const isDesk = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(min-width: 761px)').matches === true;

/** Phones: bring the recommendations into view under the sticky header (PRD §3.6). */
function scrollToRecommendations() {
  const el = document.getElementById('stage');
  if (!el) return;
  const header = document.querySelector('header')?.getBoundingClientRect().height ?? 0;
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({
    top: el.getBoundingClientRect().top + window.scrollY - header,
    behavior: reduce ? 'auto' : 'smooth',
  });
}

/**
 * Homepage (docs/prd-homepage-redesign.md §3, prototype `renderHome`): an age-first hero,
 * then the recommendation block for the chosen stage, then the existing sections.
 */
export default function Home() {
  const { t, i18n } = useTranslation(['home', 'common']);
  const location = useLocation();
  const navigate = useNavigate();
  const sel = useMemo(() => parseSelection(location.hash), [location.hash]);
  const [data, setData] = useState<{ query: string; body: RecommendationsResponse } | null>(null);
  // The query whose request failed; only shown while that query is the current one.
  const [failedQuery, setFailedQuery] = useState<string | null>(null);
  const [sheet, setSheet] = useState<'stage' | 'filters' | null>(null);

  const setSelection = useCallback(
    (next: HomeSelection) => navigate({ hash: selectionHash(next) }, { replace: true }),
    [navigate]
  );

  // Footer and shared links can land on #about.
  useEffect(() => {
    if (location.hash === '#about') document.getElementById('about')?.scrollIntoView();
  }, [location.hash]);

  const query = recommendationsQuery(sel);
  useEffect(() => {
    const controller = new AbortController();
    const api = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
    // Trying again: show loading, not the earlier failure of this same selection.
    setFailedQuery((cur) => (cur === query ? null : cur));
    fetch(`${api}/home/recommendations?${query}`, { signal: controller.signal })
      .then((res) => res.json())
      .then((res) => {
        if (res?.success && res.data) {
          setData({ query, body: res.data });
          // A later success for the same selection clears its earlier failure.
          setFailedQuery((cur) => (cur === query ? null : cur));
        } else setFailedQuery(query);
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setFailedQuery(query);
      });
    return () => controller.abort();
  }, [query]);

  const locale = i18n.language;
  // Only the response to the current selection is shown: an older one (other stage,
  // parents, other filters) would put the wrong cards under this heading and link.
  const view = useMemo<RecommendationView | null>(() => {
    if (!data || data.query !== query) return null;
    const range = selectionWindow(sel);
    const window = {
      startMs: range.from ? ptDayStartUtcMs(range.from) : undefined,
      endMs: range.to ? ptDayEndUtcMs(range.to) : undefined,
    };
    const body = data.body;
    const activities = body.activities
      .map((item) =>
        item.itemType === 'course'
          ? transformCourse(item, locale)
          : transformActivity(item, locale, window)
      )
      .filter((card): card is NonNullable<typeof card> => card !== null);
    return {
      key: data.query,
      activities,
      activitiesTotal: body.activitiesTotal,
      inPersonTotal: body.inPersonTotal,
      picks: body.picks,
      stageActivityCount: body.stageActivityCount,
    };
  }, [data, query, sel, locale]);

  const chooseAudience = (audience: AudienceKey) => {
    setSheet(null);
    setSelection({ ...sel, audience });
    if (!isDesk()) requestAnimationFrame(scrollToRecommendations);
  };

  const theme = AUDIENCE_THEME[sel.audience];
  const themeVars = {
    '--ac': theme.c,
    '--ac-on': theme.on,
    '--ac-tint': theme.tint,
    '--ac-dk': theme.dk,
  } as CSSProperties;

  return (
    <div style={themeVars} className="bg-warm-white font-text text-[15px] leading-[1.6] text-ink">
      <Helmet>
        <title>{t('home:meta.title')}</title>
        <meta name="description" content={t('home:meta.description')} />
      </Helmet>

      <section className="mx-auto grid max-w-[1160px] grid-cols-1 items-center gap-[18px] px-4 pb-2.5 pt-[22px] desk:grid-cols-[minmax(0,1.12fr)_minmax(0,0.88fr)] desk:gap-12 desk:px-7 desk:pb-[30px] desk:pt-11">
        <div>
          <h1 className="font-ui text-[28px] font-bold leading-[1.2] tracking-[-0.01em] text-ink desk:text-[clamp(32px,4vw,48px)] [text-wrap:balance]">
            {t('home:hero.titleLine1')}
            <br />
            <em className="not-italic text-primary">{t('home:hero.titleLine2')}</em>
          </h1>
          <p className="mb-4 mt-2 max-w-[32em] text-[15px] text-ink-2 desk:mb-[22px] desk:mt-3.5 desk:text-[16.5px]">
            {t('home:hero.lede')}
          </p>
          <p className="-mt-1.5 mb-3.5 flex flex-wrap gap-x-3 gap-y-1 text-[13px] text-ink-2 desk:hidden">
            <span>
              <b className="mr-[3px] font-ui text-primary-dark">{t('home:hero.familiesCount')}</b>
              {t('home:hero.families')}
            </span>
            <span>
              <b className="mr-[3px] font-ui text-primary-dark">{t('home:hero.bookingsCount')}</b>
              {t('home:hero.bookings')}
            </span>
          </p>
          <StageSelector value={sel.audience} onChange={chooseAudience} />
          <div
            aria-hidden="true"
            className="mt-1.5 hidden grid-cols-6 gap-2 font-ui text-[11px] text-mute desk:grid [&>span:last-child]:border-0 [&>span]:border-l-[1.5px] [&>span]:border-line [&>span]:pl-[3px]"
          >
            <span>0</span>
            <span>{t('home:selector.tickYears', { age: 2 })}</span>
            <span>{t('home:selector.tickYears', { age: 3 })}</span>
            <span>{t('home:selector.tickYears', { age: 5 })}</span>
            <span>{t('home:selector.tickYears', { age: 8 })}</span>
            <span />
          </div>
          <HomeFilterRow
            value={sel}
            onChange={setSelection}
            onOpenSheet={() => setSheet('filters')}
          />
        </div>
        <div className="relative hidden desk:block">
          <div className="aspect-square overflow-hidden rounded-[30px] shadow-[0_30px_60px_-34px_rgba(60,50,30,0.55)]">
            <img
              src="/home/hero.jpg"
              alt={t('home:hero.imageAlt')}
              className="h-full w-full object-cover"
            />
          </div>
          <div className="absolute -left-[18px] bottom-[22px] flex gap-[18px] rounded-2xl bg-white px-4 py-3 font-ui shadow-[0_16px_36px_-18px_rgba(60,50,30,0.5)]">
            <div>
              <b className="block text-[20px] leading-[1.1] text-primary-dark">
                {t('home:hero.familiesCount')}
              </b>
              <span className="font-text text-xs text-mute">{t('home:hero.families')}</span>
            </div>
            <div>
              <b className="block text-[20px] leading-[1.1] text-primary-dark">
                {t('home:hero.bookingsCount')}
              </b>
              <span className="font-text text-xs text-mute">{t('home:hero.bookings')}</span>
            </div>
          </div>
        </div>
      </section>

      <RecommendationBlock
        sel={sel}
        onChange={setSelection}
        view={view}
        failed={failedQuery === query}
        onOpenStageSheet={() => setSheet('stage')}
        onOpenFilterSheet={() => setSheet('filters')}
      />

      <WhySection />
      <Testimonials />
      <GiftStrip />
      <HostCta />

      <Sheet
        open={sheet === 'stage'}
        onClose={() => setSheet(null)}
        closeLabel={t('common:share.close')}
        title={t('home:filters.stageSheetTitle')}
      >
        <div className="mt-4">
          <StageSelector value={sel.audience} onChange={chooseAudience} compact />
        </div>
      </Sheet>
      <HomeFilterSheet
        open={sheet === 'filters'}
        value={sel}
        onChange={setSelection}
        onClose={() => setSheet(null)}
        onApply={() => {
          setSheet(null);
          if (!isDesk()) requestAnimationFrame(scrollToRecommendations);
        }}
      />
    </div>
  );
}
